import type { AccessPolicy } from "@/modules/dashboard/settings/types/access.types";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import { useTranslations } from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

/** How many `resource:action` chips fit a table cell before the rest collapse into `+N`. */
const VISIBLE_STATEMENTS = 4;

export interface PolicyStatementsCellProps {
  statements: AccessPolicy["statements"];
  effect: AccessPolicy["effect"];
  resourceCount: number;
}

/**
 * What a policy actually permits, as `resource:action` chips coloured by the policy's effect.
 *
 * The effect belongs to the POLICY, not to each statement — the backend has one `effect` column and every
 * statement inherits it — so all the chips in a row carry the same colour. Colouring them individually would
 * invent a distinction the data does not make.
 *
 * The summary line above them is not redundant with the chips: it counts distinct RESOURCES, which is the
 * number that says how broad the policy is when its statements are truncated.
 */
export function PolicyStatementsCell({ statements, effect, resourceCount }: PolicyStatementsCellProps) {
  const { t } = useTranslations("settings");

  const visible = statements.slice(0, VISIBLE_STATEMENTS);
  const extra = statements.length - visible.length;
  const isDeny = effect === "deny";

  return (
    <div className="flex flex-col gap-1">
      <span className="font-mono text-[10px] text-muted-foreground/70">
        {t("access_policy_stmt_summary", { permits: statements.length, resources: resourceCount })}
      </span>
      <div className="flex flex-wrap items-center gap-1">
        {visible.map((statement, index) => (
          <span
            key={`${statement.resource}:${statement.action}:${index}`}
            className={cn(
              "rounded-sm border px-1.5 py-px font-mono text-[9.5px]",
              isDeny
                ? "border-destructive/40 bg-destructive/10 text-destructive"
                : "border-primary/30 bg-primary/10 text-primary",
            )}
          >
            {statement.resource}:{statement.action}
          </span>
        ))}
        {extra > 0 && <ColocaBadge variant="secondary">+{extra}</ColocaBadge>}
      </div>
    </div>
  );
}
